import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../../styles/DeviceDeployment.css';
// Компоненты пользовательского интерфейса
import CustomButton from '../CustomElements/CustomButton';
import Selector from '../CustomElements/Selector';
import DateSelector from '../CustomElements/DateSelector';
import CustomTitle from '../CustomElements/CustomTitle';
import Input from '../CustomElements/Input';
import FormContainer from '../CustomElements/FormContainer';
import CustomContainer from '../CustomElements/CustomContainer';
import ConfirmationPage from '../CustomElements/ConfirmationPage';
import FilePicker from '../FileManager/FilePicker';
// Утилиты и хуки
import { convertToDate, transformFieldsToMoscowStart } from '../../utils/convertToDate';
import { useButtonValidation } from '../../customHook/useButtonValidation';
import { handleUniversalChange } from '../../utils/formUtils';
import useFormDataChanged from '../../customHook/useFormDataChanged';
import useOptionsLoader from '../../customHook/useOptionsLoader';
import useSuccessListener from '../../customHook/useSuccessListener';
// Действия Redux
import { load } from '../../redux/actions/loader.action';
import { editOperationStatusFromServer } from '../../redux/actions/statuses.action';
import {
  initDepartmentOptionsFromServer,
  initOptionsDirectoratesUippsFromServer,
} from '../../redux/actions/options.action';
import { prepareObjectForSubmit } from '../../utils/prepareObjectForSubmit';
import { omitFields } from '../../utils/omitFields';
import { convertStringValuesToNumbers } from '../../utils/convertStringValuesToNumbers';

// компонент  изменения статуса уиппс 'эксплуатация выдача'

function EditUsageSendUippsStatus({ currentStatus, currentDevice, closeModal, title, t }) {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();
  // получаем опции из стора
  const optionsForSelect = useSelector((state) => state.options);

  // Состояние для формы. Мемоизируем начальные данные формы
  const initialFormData = useMemo(
    () => ({
      id: currentStatus?.id || '',
      deviceId: currentStatus?.deviceId || '',
      typeId: currentDevice?.typeId || '',
      date: convertToDate(currentStatus?.date) || '',
      roadId: currentStatus?.roadId || '',
      acceptanceCert: currentStatus?.acceptanceCert || '',
      acceptanceCertName: currentStatus?.acceptanceCertName || '',
      directorateId: currentStatus?.directorateId || '',
      departmentId: currentStatus?.departmentId || '',
      trainTypeId: currentStatus?.trainTypeId || '',
      trainNumber: currentStatus?.trainNumber || '',
      responsibleId: currentStatus?.driverId || '',
      responsibleContact: currentStatus?.responsibleContact || '',
      placement: currentStatus?.placement,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentStatus]
  );

  const [formData, setFormData] = useState(initialFormData);

  // Состояния для кнопки и уведомления об успешном добавлении
  const [addSuccess, setAddSuccess] = useState(false);

  // Состояние для массива обязательных полей
  const [optionalFields] = useState(['responsibleId', 'responsibleContact', 'trainTypeId', 'trainNumber', 'placement']);

  // Функция для отмены подтверждения и закрытия модального окна
  const closeStatusEditModal = () => setAddSuccess(false);

  // Функция для открытия модального окна подтверждения
  const handlerOpenModal = () => setAddSuccess(true);

  //проверяем были ли изменения в форме
  const isFormEdit = useFormDataChanged(initialFormData, formData, true);

  /** Функция для проверки валидности формы */
  const isButtonDisabled = useButtonValidation(formData, optionalFields);

  // Обработчик клика
  const handlerSubmit = (event) => {
    event.preventDefault();
    handlerOpenModal();
  };

  // отправка данных на сервер при изменении статуса
  const handlerSubmitEdit = (event) => {
    event.preventDefault();
    //переводим в состояние загрузки
    dispatch(load());
    // обрабатываем пустые строки
    const formDataWithEmptyStrings = prepareObjectForSubmit(currentStatus, formData);
    // удаляем поля, которые не нужно отправлять
    const formWithoutExcluded = omitFields(formDataWithEmptyStrings, [
      'id',
      'roadId',
      'deviceId',
      'acceptanceCertName',
      'directorateId',
    ]);
    // Преобразуем указанные поля в начало дня по Москве
    const payload = transformFieldsToMoscowStart(formWithoutExcluded, ['date']);
    // преобразуем строковые значения в числа
    const transformedPayload = convertStringValuesToNumbers(payload, ['responsibleId']);
    // Отправляем данные на сервер через Redux
    dispatch(
      editOperationStatusFromServer({
        payload: transformedPayload,
        statusId: formData?.id,
        deviceId: formData?.deviceId,
        typeId: formData?.typeId,
        path: 'usage',
      })
    );
  };

  //хук для запроса опций для дирекций и контроля селекта
  const messageForSelectDirectorates = t?.messageForSelectDirectorates || 'Выберите дорогу';
  const directoratesControl = useOptionsLoader(
    formData,
    setFormData,
    ['directorateId', 'departmentId'],
    'roadId',
    initOptionsDirectoratesUippsFromServer,
    messageForSelectDirectorates
  );
  //хук для запроса опций для подразделений и контроля селекта
  const messageForSelectDepartments = t?.messageForSelectDepartments || 'Выберите дирекцию';
  const departmentsControl = useOptionsLoader(
    formData,
    setFormData,
    ['departmentId'],
    'directorateId',
    initDepartmentOptionsFromServer,
    messageForSelectDepartments
  );

  //отслеживаем выполнение запроса
  useSuccessListener(setFormData, initialFormData, closeModal, closeStatusEditModal);

  return (
    <CustomContainer>
      <CustomTitle titleText={title} deviceType={currentDevice.type} deviceId={currentDevice.id} />
      {addSuccess && (
        <ConfirmationPage
          confirmText={t?.confirmEditUippsStatusText || 'Вы уверены, что хотите изменить данные статуса УИППС?'}
          confirmButtonText={t?.confirmButton || 'Изменить'}
          cancelButtonText={t?.cancelButton || 'Отменить'}
          onConfirm={handlerSubmitEdit} // Функция, которая должна быть вызвана при подтверждении
          onCancel={closeStatusEditModal} // Функция для закрытия модального окна или отмены действия
        />
      )}
      {!addSuccess && (
        <>
          <FormContainer>
            <DateSelector
              column="left"
              width="100%"
              height="var(--sadr-font-size-60)"
              label={t?.labelDeploymentDate || 'Дата выдачи в эксплуатацию *'}
              selectedDate={formData.date}
              setSelectedDate={(date) => handleUniversalChange(formData, setFormData, null, 'date', date, 'date')}
              name="date"
              placeholder={t?.placeholderChooseDate || 'Выберите дату выдачи'}
              minDate={convertToDate(currentDevice?.lastActionDate)}
              maxDate={new Date()}
            />
            <FilePicker
              column="left"
              label={t?.labelAcceptanceCert || 'Акт приема-передачи *'}
              formData={formData}
              setFormData={setFormData}
              fieldName="acceptanceCertName"
              fieldIdName="acceptanceCert"
              rowSpan={2}
            />
            {!currentStatus?.placement && (
              <Input
                onChange={(event) => handleUniversalChange(formData, setFormData, event)}
                value={formData?.responsibleId || ''}
                name="responsibleId"
                column="left"
                width="100%"
                height="var(--sadr-font-size-60)"
                placeholder={t?.placeholderEmployeeNumber || 'Введите табельный номер'}
                label={t?.labelEmployeeNumber || 'Табельный № машиниста'}
                format="######"
                maxLength={9}
                isCoordinate={true}
                isAllowed={undefined}
              />
            )}
            {currentStatus?.placement && (
              <Selector
                onChange={(selectedOption) => {
                  handleUniversalChange(formData, setFormData, null, 'trainTypeId', selectedOption, 'select');
                }}
                value={
                  formData.trainTypeId
                    ? optionsForSelect?.trainTypes?.find((option) => option.value === formData.trainTypeId)
                    : null
                }
                name="trainTypeId"
                column="left"
                options={optionsForSelect?.trainTypes}
                width="100%"
                height="var(--sadr-font-size-60)"
                placeholder={t?.placeholderTrainType || 'Выберите тип подвижного состава'}
                label={t?.labelTrainType || 'Тип подвижного состава'}
                isMulti={false}
              />
            )}
            <Selector
              column="right"
              options={optionsForSelect?.roads}
              onChange={(selectedOption) => {
                handleUniversalChange(formData, setFormData, null, 'roadId', selectedOption, 'select');
              }}
              value={
                formData.roadId ? optionsForSelect?.roads?.find((option) => option.value === formData.roadId) : null
              }
              name="roadId"
              label={`${t?.labelRoad} *` || 'Дорога *'}
              placeholder={t?.placeholderRoad || 'Выберите дорогу'}
              width="100%"
              height="var(--sadr-font-size-60)"
              isMulti={false}
            />
            <Selector
              column="right"
              options={optionsForSelect?.directoratesUipps}
              onChange={(selectedOption) => {
                handleUniversalChange(formData, setFormData, null, 'directorateId', selectedOption, 'select');
              }}
              value={
                formData.directorateId
                  ? optionsForSelect?.directoratesUipps?.find((option) => option.value === formData.directorateId)
                  : null
              }
              placeholder={t?.placeholderDirectorate || 'Выберите дирекцию'}
              label={`${t?.labelDirectorate} *` || 'Дирекция *'}
              width="100%"
              height="var(--sadr-font-size-60)"
              isMulti={false}
              name="directorateId"
              isDisabled={directoratesControl?.isSelectorDisabled}
              message={directoratesControl?.message}
              customClick={directoratesControl?.onSelectorChange}
            />
            <Selector
              column="right"
              options={optionsForSelect?.departments}
              onChange={(selectedOption) => {
                handleUniversalChange(formData, setFormData, null, 'departmentId', selectedOption, 'select');
              }}
              value={
                formData.departmentId
                  ? optionsForSelect?.departments?.find((option) => option.value === formData?.departmentId)
                  : null
              }
              placeholder={t?.placeholderSelectDepartment || 'Выберите подразделение'}
              label={`${t?.labelDepartment} *` || 'Подразделение *'}
              width="100%"
              height="var(--sadr-font-size-60)"
              isMulti={false}
              name="departmentId"
              isDisabled={departmentsControl?.isSelectorDisabled}
              message={departmentsControl?.message}
              customClick={departmentsControl?.onSelectorChange}
            />
            {!currentStatus?.placement && (
              <Input
                column="right"
                onChange={(event) => handleUniversalChange(formData, setFormData, event)}
                value={formData?.responsibleContact || ''}
                label={t?.labelDriverContact || 'Контакты машиниста'}
                placeholder={t?.placeholderDriverContact || 'Введите контакты'}
                width="100%"
                height="var(--sadr-font-size-60)"
                name="responsibleContact"
                maxLength={20}
              />
            )}
            {currentStatus?.placement && (
              <Input
                column="right"
                onChange={(event) => handleUniversalChange(formData, setFormData, event)}
                value={formData?.trainNumber || ''}
                label={t?.labelTrainNumber || 'Номер подвижного состава'}
                placeholder={t?.placeholderTrainNumber || 'Введите № подвижного состава'}
                width="100%"
                height="var(--sadr-font-size-60)"
                name="trainNumber"
                maxLength={20}
              />
            )}
          </FormContainer>
          <CustomButton
            onClick={(event) => {
              handlerSubmit(event);
            }}
            width="var(--sadr-font-size-420)"
            height="var(--sadr-font-size-60)"
            text={t?.buttonSaveChanges || 'Сохранить изменения'}
            disabled={isButtonDisabled || !isFormEdit}
            isButton
          />
        </>
      )}
    </CustomContainer>
  );
}

export default EditUsageSendUippsStatus;
